// Small layout pieces shared by every screen.
import { useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export const COLORS: Record<string, string> = {
  teal: '#0d9488',
  blue: '#2563eb',
  violet: '#7c3aed',
  rose: '#e11d48',
  orange: '#ea580c',
  amber: '#ca8a04',
  green: '#16a34a',
  slate: '#475569',
};

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin', className)} aria-hidden="true" />;
}

export function PageHeader({ title, sub, action, back }: { title: string; sub?: ReactNode; action?: ReactNode; back?: ReactNode }) {
  return (
    <header className="flex items-end justify-between gap-3 pt-2">
      <div className="min-w-0 flex-1">
        {back}
        <h1 className="text-[30px] leading-tight font-bold tracking-tight">{title}</h1>
        {sub && <p className="text-muted-foreground mt-1 text-[15px]">{sub}</p>}
      </div>
      {action}
    </header>
  );
}

export function Section({ title, aside, children, className }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('grid gap-2.5', className)}>
      {(title || aside) && (
        <div className="flex items-center justify-between gap-2 px-1">
          <h2 className="text-muted-foreground flex items-center gap-2 text-[13px] font-semibold tracking-wide uppercase">{title}</h2>
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/** A rounded group of rows separated by hairlines. */
export function Group({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('bg-card divide-border divide-y overflow-hidden rounded-2xl border', className)}>{children}</div>;
}

export function Row({
  children,
  onClick,
  className,
  as: As = onClick ? 'button' : 'div',
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  as?: 'button' | 'div' | 'label';
}) {
  return (
    <As
      onClick={onClick}
      className={cn(
        'flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left',
        onClick && 'active:bg-muted cursor-pointer transition-colors',
        className,
      )}
    >
      {children}
    </As>
  );
}

export function RowText({ title, sub, className }: { title: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0 flex-1', className)}>
      <div className="font-medium">{title}</div>
      {sub && <div className="text-muted-foreground mt-0.5 text-[13px]">{sub}</div>}
    </div>
  );
}

export function IconDisc({ children, tone = 'muted', className }: { children: ReactNode; tone?: 'muted' | 'accent'; className?: string }) {
  return (
    <span
      className={cn(
        'grid size-10 shrink-0 place-items-center rounded-full [&_svg]:size-[18px]',
        tone === 'accent' ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground',
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A person: their photo, or the first letter of their name on their colour. */
export function Avatar({ name, color, photo, size = 'md' }: { name: string; color: string; photo?: string | null; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const [broken, setBroken] = useState(false);
  const initial = [...(name || '?').trim()][0]?.toUpperCase() ?? '?';
  const dims = { sm: 'size-5 text-[10px]', md: 'size-9 text-[15px]', lg: 'size-14 text-[22px]', xl: 'size-24 text-[36px]' }[size];
  if (photo && !broken) {
    return <img src={photo} alt="" aria-hidden="true" onError={() => setBroken(true)} className={cn('bg-muted shrink-0 rounded-full object-cover', dims)} />;
  }
  return (
    <span aria-hidden="true" className={cn('grid shrink-0 place-items-center rounded-full font-bold text-white', dims)} style={{ background: COLORS[color] ?? COLORS.teal }}>
      {initial}
    </span>
  );
}

/** A thin progress bar that animates to its value. */
export function Bar({ value, tone = 'primary', className }: { value: number; tone?: 'primary' | 'health'; className?: string }) {
  const v = Math.max(0, Math.min(1, value));
  const color = tone === 'health' ? (v >= 0.66 ? 'bg-good' : v >= 0.4 ? 'bg-ok' : 'bg-bad') : 'bg-primary';
  return (
    <div className={cn('bg-muted h-1.5 overflow-hidden rounded-full', className)}>
      <motion.div className={cn('h-full rounded-full', color)} initial={{ width: 0 }} animate={{ width: `${v * 100}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
    </div>
  );
}

/** Segmented progress for steps (Chime's setup checklist): one bar per step, filled when done. */
export function Steps({ done, total, className }: { done: number; total: number; className?: string }) {
  return (
    <div className={cn('flex gap-1', className)} role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={cn('h-1.5 flex-1 rounded-full transition-colors', i < done ? 'bg-primary' : 'bg-muted')} />
      ))}
    </div>
  );
}

export const pct = (x: number) => `${Math.round(Math.max(0, Math.min(1, x)) * 100)}%`;

/** Fades and lifts children in, one after another. */
export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="show"
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
    >
      {children}
    </motion.div>
  );
}

export const staggerItem = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { type: 'spring' as const, stiffness: 300, damping: 30 } },
};
